import React, { useCallback, useEffect, useState } from 'react';
import { getWatiSettings, setWatiSwitch } from '../api/wati';

function Toggle({ on, busy, onClick, label }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label}
      disabled={busy} onClick={onClick}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${on ? 'bg-zoddle-green' : 'bg-gray-300'}`}>
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${on ? 'translate-x-5' : 'translate-x-0.5'}`} />
    </button>
  );
}

function fmt(iso) {
  if (!iso) return '—';
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

export default function WatiTemplates() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try { setData(await getWatiSettings()); setError(null); }
    catch (e) { setError(e.response?.data?.detail || 'Could not load WATI settings'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function flip(key, enabled) {
    setBusy(key);
    try {
      await setWatiSwitch(key, enabled);
      await load();
    } catch (e) { setError(e.response?.data?.detail || 'Could not change that switch'); }
    finally { setBusy(null); }
  }

  function flipGlobal() {
    const turningOn = !data.global_enabled;
    const msg = turningOn
      ? 'Switch WATI ON? Every template that is on below will start sending.'
      : 'Switch WATI OFF? Nothing will be sent, and messages already due are skipped (not held).';
    if (window.confirm(msg)) flip('GLOBAL', turningOn);
  }

  if (!data) {
    return <div className="p-6 text-sm text-gray-500">{error || 'Loading…'}</div>;
  }
  const onCount = data.templates.filter(t => t.enabled).length;

  return (
    <div className="p-6 max-w-5xl">
      {error && <div className="mb-4 rounded-lg bg-red-50 border border-zoddle-pink text-zoddle-pink text-sm px-4 py-2">{error}</div>}

      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-5 flex items-center gap-4">
        <Toggle on={data.global_enabled} busy={busy === 'GLOBAL'} onClick={flipGlobal} label="WATI master switch" />
        <div className="flex-1">
          <div className="font-semibold text-gray-800">WATI master switch — {data.global_enabled ? 'ON' : 'OFF'}</div>
          <div className="text-xs text-gray-500">
            Kill switch for every WhatsApp message. A template sends only when this and its own switch are both on.
          </div>
        </div>
        <div className="flex gap-2 text-xs font-semibold">
          <span className={`px-2 py-1 rounded-full ${data.configured ? 'bg-green-100 text-green-700' : 'bg-red-100 text-zoddle-pink'}`}>
            {data.configured ? 'WATI connected' : 'WATI credentials missing'}
          </span>
          {data.dry_run && <span className="px-2 py-1 rounded-full bg-yellow-100 text-yellow-700">Dry run</span>}
          <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-600">{onCount} of {data.templates.length} on</span>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-2 w-16">On</th>
              <th className="px-4 py-2">Template</th>
              <th className="px-4 py-2">Sent when</th>
              <th className="px-4 py-2">Variables</th>
              <th className="px-4 py-2">Changed</th>
            </tr>
          </thead>
          <tbody>
            {data.templates.map(t => (
              <tr key={t.template_key} className="border-t border-gray-100">
                <td className="px-4 py-3">
                  <Toggle on={t.enabled} busy={busy === t.template_key} label={t.template_key}
                    onClick={() => flip(t.template_key, !t.enabled)} />
                </td>
                <td className="px-4 py-3 font-mono text-xs text-gray-800">
                  {t.template_key}
                  {t.wati_template_name !== t.template_key && (
                    <div className="text-gray-400">sends as {t.wati_template_name}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-600">{t.when}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{t.params.length ? t.params.join(', ') : 'none'}</td>
                <td className="px-4 py-3 text-xs text-gray-400">{fmt(t.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.other_rows.length > 0 && (
        <div className="mt-5">
          <div className="text-xs font-semibold text-gray-500 mb-1">
            Other rows in the table (old or test names no template uses — no effect)
          </div>
          <div className="flex flex-wrap gap-2">
            {data.other_rows.map(r => (
              <span key={r.template_key} className="px-2 py-1 rounded-full bg-gray-100 text-gray-500 text-xs font-mono">
                {r.template_key} · {r.enabled ? 'on' : 'off'}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
